/**
 * Where a case comes from: the lab's own market, abroad, or online.
 *
 * Section 1 of the request of 24 September 2026 put these three in place of the
 * job order's country. The picker had offered every country in the world, six
 * of them first, and the lab asked for the three things it actually tells cases
 * apart by. The server keeps the country each case was taken in under, and gave
 * every one of them an origin from it once: the UAE local, anywhere else
 * international. Online is not a country, so a case is online only when somebody
 * says so.
 *
 * In the client's order, which is the order every picker offers them in and the
 * order the origin sorts group by.
 */
export const ORIGINS = ['local', 'international', 'online'] as const;
export type Origin = (typeof ORIGINS)[number];

/** How each origin reads, on a screen and on the printed sheet. */
export const ORIGIN_LABEL: Record<Origin, string> = {
  local: 'Local',
  international: 'International',
  online: 'Online',
};

/** Every new case starts local, as the country started at the UAE: most of the work is. */
export const DEFAULT_ORIGIN: Origin = 'local';

/** The refusal, worded once for the form, the mock server and the API, which sends the same. */
export const ORIGIN_REFUSAL = {
  choose: 'Choose local, international or online.',
} as const;

export function isOrigin(value: string): value is Origin {
  return (ORIGINS as readonly string[]).includes(value);
}

/**
 * The sort keys that put one origin's cases first. Section 2 of the request of
 * 24 September 2026: "allow sorting cases in orders, production line and
 * archive based on these".
 *
 * Named orders rather than a column and a direction, like every preset: there
 * is no direction in which International comes first. The API takes the same
 * three keys on `GET /job-orders`, `GET /kits` and `GET /archive`, because all
 * three lists are paged and a page sorted in the browser would put the origin
 * first on page one and again on page two.
 */
export const ORIGIN_FIRST = {
  local: 'localFirst',
  international: 'internationalFirst',
  online: 'onlineFirst',
} as const satisfies Record<Origin, string>;

export type OriginFirstKey = (typeof ORIGIN_FIRST)[Origin];

/** The origin a sort key puts first, or null for any other key. */
export function originFirstOf(sortKey: string | null | undefined): Origin | null {
  return ORIGINS.find((origin) => ORIGIN_FIRST[origin] === sortKey) ?? null;
}

/**
 * Where a case falls when `first` leads: that origin at 0, then the other two
 * in the client's order, then anything unknown. The API's `Origins.Rank`, so a
 * page sorted here agrees with the order the server sent it in.
 */
export function originRank(origin: string | null | undefined, first: Origin): number {
  if (origin === first) return 0;
  const index = (ORIGINS as readonly string[]).indexOf(origin ?? '');
  return index < 0 ? ORIGINS.length + 1 : index + 1;
}
